<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import {
    cityDailyScore,
    copyToClipboard,
    formatDailyAverageScore,
    formatDistance,
    formatHms,
    localDateString,
    msUntilLocalMidnight,
    scoreBandEmoji,
    type CityDailyRoundResult,
    type ShareContent,
  } from '@atlasguess/games';

  interface Props {
    /** Per-round results, in play order. Drives the emoji grid + score. */
    rounds: CityDailyRoundResult[];
    /**
     * Per-round display info for the breakdown table — the formatted
     * city line for each round, parallel to `rounds`. The mode owner
     * builds this from its planned cities + the countries map;
     * EndScreen stays agnostic of the dataset shape.
     */
    roundDetails: { primary: string }[];
    /** Locale-formatted date label for the share text + headline ("April 24"). */
    dateLabel: string;
    /**
     * Rich + plain share content the Share button puts on the clipboard.
     * Rich form has "AtlasGuess.com" as a clickable link to the mode URL.
     */
    shareContent: ShareContent;
    /** Display unit for the distance summary readout. */
    unitSystem: 'miles' | 'km';
    /** URL to navigate to when the user taps "Practice mode". */
    practiceHref: string;
  }

  let { rounds, roundDetails, dateLabel, shareContent, unitSystem, practiceHref }: Props = $props();

  let copied = $state(false);
  let copyResetTimer: ReturnType<typeof setTimeout> | undefined;

  // Tick the countdown every second so "5:23:11" keeps refreshing. When
  // local midnight crosses, reload the page so the daily mounts fresh on
  // the new day's puzzle — otherwise a player who leaves the end screen
  // open across midnight is stuck looking at yesterday's results forever.
  // Also fires on tab-visible since browsers throttle background intervals.
  const initialDate = localDateString();
  let countdownMs = $state(msUntilLocalMidnight());
  let countdownInterval: ReturnType<typeof setInterval> | undefined;

  function checkDayRollover(): void {
    if (localDateString() !== initialDate) {
      window.location.reload();
    }
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') checkDayRollover();
  }

  onMount(() => {
    countdownInterval = setInterval(() => {
      countdownMs = msUntilLocalMidnight();
      checkDayRollover();
    }, 1000);
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });

  onDestroy(() => {
    if (countdownInterval) clearInterval(countdownInterval);
    if (copyResetTimer) clearTimeout(copyResetTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
  });

  const scores = $derived(rounds.map((r) => cityDailyScore(r.distanceKm)));
  const totalScore = $derived(scores.reduce((sum, s) => sum + s, 0));
  // Headline number = 0..100 average per round, fixed-2 dp. Same
  // shape used on the leaderboard, share grid, stats, and profile so
  // the player sees a consistent score across surfaces.
  const scoreLabel = $derived(formatDailyAverageScore(totalScore, rounds.length));

  // Average distance is kept as a secondary readout — the score is the
  // primary signal for daily, but a player who's curious about how far
  // off they were on average can still see it at a glance.
  const avgKm = $derived(
    rounds.length === 0
      ? 0
      : rounds.reduce((sum, r) => sum + r.distanceKm, 0) / rounds.length,
  );
  const avgLabel = $derived(formatDistance(avgKm, unitSystem));

  async function handleShare(): Promise<void> {
    const ok = await copyToClipboard(shareContent);
    if (!ok) return;
    copied = true;
    if (copyResetTimer) clearTimeout(copyResetTimer);
    copyResetTimer = setTimeout(() => {
      copied = false;
    }, 2000);
  }

  function goToPractice(): void {
    navigate(practiceHref);
  }
</script>

<div class="end-screen">
  <div class="end-screen__panel">
    <div class="end-screen__date">{dateLabel}</div>

    <table class="end-screen__breakdown" aria-label="Per-round breakdown">
      <tbody>
        {#each rounds as round, i (round.cityId)}
          <tr>
            <td class="end-screen__breakdown-name">
              <span class="end-screen__breakdown-city">{roundDetails[i]?.primary ?? ''}</span>
            </td>
            <td class="end-screen__breakdown-distance">
              {formatDistance(round.distanceKm, unitSystem)}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>

    <div class="end-screen__grid" aria-label="Per-round result grid">
      {#each rounds as round, i (round.cityId)}
        <span class="end-screen__cell">
          <span class="end-screen__cell-emoji">{scoreBandEmoji(scores[i], i)}</span>
          <span class="end-screen__cell-score">{scores[i]}</span>
        </span>
      {/each}
    </div>

    <div class="end-screen__total">
      <strong>{scoreLabel}</strong>
      <span class="end-screen__total-sep">·</span>
      avg <strong>{avgLabel}</strong>
    </div>

    <div class="end-screen__actions">
      <button class="end-screen__btn end-screen__btn--share" onclick={handleShare}>
        {copied ? 'Copied!' : 'Share'}
      </button>
      <button class="end-screen__btn end-screen__btn--practice" onclick={goToPractice}>
        Go to Practice Mode
      </button>
    </div>

    <div class="end-screen__countdown">
      Come back in {formatHms(countdownMs)}
    </div>
  </div>
</div>
